/**
 * @file notificationTemplateService.js
 * @description Service for handling multitenant notification template requests to the System API.
 */

import { systemHttp } from './httpService';

/**
 * Fetches all multitenant notification templates.
 * @returns {Promise<Object>} - A promise that resolves to the notification templates payload.
 * @throws {Error} - If there is an error fetching the notification templates.
 */
export const getNotificationTemplates = async () => {
    try {
        const response = await systemHttp.get(
            '/v2/system/notification-template',
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching notification templates:', error);
        throw error;
    }
};

/**
 * Fetches a single notification template by ID.
 * @param {number|string} notificationId - The ID of the notification template.
 * @returns {Promise<Object>} - A promise that resolves to the notification template.
 * @throws {Error} - If there is an error fetching the notification template.
 */
export const getNotificationTemplateById = async notificationId => {
    try {
        const response = await systemHttp.get(
            `/v2/system/notification-template/${notificationId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching notification template:', error);
        throw error;
    }
};

/**
 * Creates a new notification template.
 * @param {Object} payload - The data for the new notification template.
 * @returns {Promise<Object>} - A promise that resolves to the created notification template.
 * @throws {Error} - If there is an error creating the notification template.
 */
export const createNotificationTemplate = async payload => {
    try {
        const response = await systemHttp.post(
            '/v2/system/notification-template',
            payload,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating notification template:', error);
        throw error;
    }
};

/**
 * Updates an existing notification template.
 * @param {Object} payload - The notification template data to update.
 * @returns {Promise<Object>} - A promise that resolves to the updated notification template.
 * @throws {Error} - If there is an error updating the notification template.
 */
export const updateNotificationTemplate = async payload => {
    try {
        const response = await systemHttp.put(
            '/v2/system/notification-template',
            payload,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating notification template:', error);
        throw error;
    }
};

/**
 * Deletes a notification template by ID.
 * @param {number|string} notificationId - The ID of the notification template to delete.
 * @returns {Promise<Object|null>} - A promise that resolves to the response data or null.
 * @throws {Error} - If there is an error deleting the notification template.
 */
export const deleteNotificationTemplate = async notificationId => {
    try {
        const response = await systemHttp.delete(
            `/v2/system/notification-template/${notificationId}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error deleting notification template:', error);
        throw error;
    }
};

/**
 * Normalizes a (possibly thin) API template response onto the full editable shape the UI expects.
 * TODO(PR #887 contract): NotificationTemplateViewModel does not yet guarantee it returns the
 * content/audit fields below (subject, htmlBody, plainTextBody, pushBody, pushDestinationTypeId,
 * pushDestinationValue, createdBy/createdAt, updatedBy/updatedAt). Rather than invent that data,
 * missing fields default to safe empty values here until the backend contract is finalized.
 * @param {Object} template - The raw template returned by the API.
 * @returns {Object} - The template normalized for the editor form.
 */
export const mapTemplateForEdit = (template = {}) => ({
    id: template.id,
    name: template.name || '',
    description: template.description ?? null,
    notificationCategoryId: template.notificationCategoryId ?? null,
    agencyId: template.agencyId ?? 0,
    availableTierIds: template.availableTierIds || [],
    notificationStatusId: template.notificationStatusId ?? 1,
    subject: template.subject ?? '',
    htmlBody: template.htmlBody ?? '',
    plainTextBody: template.plainTextBody ?? '',
    pushBody: template.pushBody ?? '',
    pushDestinationTypeId: template.pushDestinationTypeId ?? null,
    pushDestinationValue: template.pushDestinationValue ?? null,
    createdAt: template.createdAt ?? null,
    createdBy: template.createdBy ?? null,
    updatedAt: template.updatedAt ?? null,
    updatedBy: template.updatedBy ?? null,
});

export {
    getNotificationCategories,
    getPushDestinationTypes,
} from './messageService';
export { getAgencies, fetchTenantTiers } from './systemService';
